// @flow

import React, {useState, useEffect} from "react";
import {useFormik} from "formik";
import {useObserver} from "mobx-react-lite";
import {useHistory} from "react-router-dom";
import moment from "moment";
import {Row, Col, Form, Input, Select, Button, Dropdown, Menu, Modal, Switch, Upload} from "antd";
import {EllipsisOutlined, UploadOutlined, DeleteOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import placeholder from "../../../public/images/company-placeholder.jpg";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {newCompanyValidation} from "./newCompanyValidation";
import {newCompanyFormType, newCompanyPropType} from "./newCompanyPropTypes";
import {ImgBg, IconContainer, MarkerImg, DeleteIcon} from "./newCompanyStyles";
import {
    counties,
    courierTypes,
    workingTypes,
    smartLockerProviders
} from "../../../constants/constants";
import {ImageEditorModal} from "../../_defaultComponents/ImageEditorModal/ImageEditorModal";
import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import config from "../../../_core/config";

export const NewCompany = ({companyId}: newCompanyPropType) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["newCompany", "courierTypes"]);
    const [editorVisible, setEditorVisible] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [companyPhoto, setCompanyPhoto] = useState("");
    const [companyMarkerImage, setCompanyMarkerImage] = useState();
    const [img, setImg] = useState("");
    const [photoLogo, setPhotoLogo] = useState("");

    const [loadingCompany, company, setCompany, setLoadingCompany] = useFetch({
        fetchFn: stores.companyStore.getCompany.bind(null, {companyId, withSubCompanies: true}),
        showLoading: true,
        condition: !!companyId
    });

    const [loadingGenerateCode, generateCode] = useFetch({
        fetchFn: stores.companyStore.generateCompanyCode.bind(null, {
            companyId,
            withSubCompanies: true
        }),
        condition: !companyId
    });

    useEffect(() => {
        setCompanyMarkerImage(company?.data?.markerImage);
    }, [company]);

    const formik = useFormik({
        initialValues: {
            companyName: "",
            companyCode: generateCode?.companyCode || "",
            address: "",
            city: "",
            county: "",
            bankName: "",
            iban: "",
            registeredTaxOffice: "",
            taxNumber: "",
            billInformation: "",
            courierTypes: [],
            targetDeliveryTime: 45,
            validateSubCompanies: false,
            isApiIntegrationActive: false,
            workingTypes: [],
            callback: {
                url: "",
                tokenHeader: "",
                token: ""
            },
            isOrderPreparedActive: false,
            isManuelDedicationActive: false,
            isTestUser: false,
            isAssignmentByDeliveryPointActive: false,
            isSellerCoordinateRequirementActive: false,
            locationUpdateInterval: null,
            heartbeatInterval: null,
            courierSearchDistance: 10000,
            options: {
                merge: company?.data?.options?.merge || [
                    {
                        courierType: "motorCourier",
                        isActive: false
                    },
                    {
                        courierType: "carCourier",
                        isActive: false
                    }
                ],
                proof: company?.data?.options?.proof || {
                    isActive: false,
                    requiredStep: "both",
                    requiredType: "both",
                    labels: [
                        {
                            name: "photo"
                        }
                    ]
                }
            },
            ...company?.data,
            photo: undefined,
            smartLockerProvider: company?.data?.smartLockerProvider || null
        },
        validationSchema: newCompanyValidation,
        enableReinitialize: true,
        onSubmit: async (values: newCompanyFormType) => {
            await setLoadingCompany(true);
            if (photoLogo) {
                const uploadLogo = await new Promise(resolve => {
                    const reader = new FileReader();
                    reader.readAsDataURL(photoLogo);
                    reader.onloadend = () => {
                        const base64String = reader.result.split(",")[1];
                        const payload = {
                            file: {
                                originalname: photoLogo.name,
                                mimetype: photoLogo.type,
                                buffer: base64String
                            },
                            directory: "desktop"
                        };
                        resolve(payload);
                        onCancel();
                    };
                    reader.onerror = error => reject(error);
                });
                const result = await stores.companyStore.upload(uploadLogo);
                setFieldValue("photo", result.result.key);
                setImg(result.result.key);
                values.photo = result.result.key;
            } else if (values.photoId) {
                values.photo = values.photoId;
            }
            if (companyMarkerImage) {
                if (company?.data?.markerImage !== companyMarkerImage) {
                    const resultOfRequest = await stores.companyStore.uploadMarkerImage(
                        companyMarkerImage
                    );
                    values.markerImage = resultOfRequest.imageId;
                }
            } else {
                values.markerImage = null;
            }

            await handleCompanySubmit(values);
        }
    });

    const handleCompanySubmit = async (values: newCompanyFormType) => {
        if (companyId) {
            try {
                const result = await stores.companyStore.updateCompany(values);
                setLoadingCompany(false);
                if (result) {
                    history.push("/companies/" + companyId);
                }
            } catch (e) {
                console.log(e);
            }
        } else {
            values.createdAt = moment(new Date()).format("YYYY-MM-DD");
            const result = await stores.companyStore.createCompany(values);
            setLoadingCompany(false);
            if (result) {
                history.push("/companies/" + result._id);
            }
        }
    };

    const dummyRequest = ({file, onSuccess}) => {
        setCompanyMarkerImage(file);
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    };

    const onRemoveMarkerImage = () => {
        setCompanyMarkerImage();
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    return useObserver(() => (
        <Row type="flex" justify="center" data-testid="newCompanyScreen">
            <ImageEditorModal
                visible={editorVisible}
                setVisible={value => setEditorVisible(value)}
                setStatePhoto={img => {
                    setCompanyPhoto(img);
                }}
                setPhoto={photoId => {
                    setFieldValue("photo", photoId);
                }}
                isCompany
                logoImg={img}
                setPhotoLogo={setPhotoLogo}
                companyPhotoId={company?.data?.photoId}
            />
            <Modal
                visible={showImage}
                data-testid="profilePhotoModal"
                onCancel={() => setShowImage(false)}
                footer={null}
            >
                <Row type="flex" justify="center">
                    <img
                        style={{overflow: "hidden", padding: "10px"}}
                        data-testid="profilePhoto"
                        src={
                            values?.photo
                                ? values?.photo
                                : company?.data?.photoId
                                ? `${config.awsUrl}${company?.data?.photoId}`
                                : placeholder
                        }
                    />
                </Row>
            </Modal>
            <Col span={20}>
                <Form
                    autoComplete="off"
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                    onFinish={handleSubmit}
                    data-testid="newCompanyForm"
                >
                    <ErrorFocus formik={formik} />
                    <Row type="flex" justify="center">
                        <ImgBg
                            isEdit={!!companyId}
                            onClick={companyId ? () => {} : () => setEditorVisible(true)}
                            src={
                                values?.photo
                                    ? values?.photo
                                    : company?.data?.photoId
                                    ? `${config.awsUrl}${company?.data?.photoId}`
                                    : placeholder
                            }
                            data-testid="profilePhoto"
                        >
                            {companyId && (
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            {(values.photo || values.photoId) && (
                                                <Menu.Item
                                                    onClick={() => setShowImage(true)}
                                                    data-testid="profilePhoto_open"
                                                >
                                                    {t("actions.open", {ns: "common"})}
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                onClick={() => setEditorVisible(true)}
                                                data-testid="profilePhoto_update"
                                            >
                                                {t("actions.update", {ns: "common"})}
                                            </Menu.Item>
                                        </Menu>
                                    }
                                >
                                    <IconContainer>
                                        <EllipsisOutlined />
                                    </IconContainer>
                                </Dropdown>
                            )}
                            {!values.photo && !companyId && (
                                <CustomButton data-testid="profilePhoto_add">
                                    {t("actions.addImage", {ns: "common"})}
                                </CustomButton>
                            )}
                        </ImgBg>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.companyName", {ns: "common"})}
                                validateStatus={
                                    errors.companyName && touched.companyName ? "error" : "success"
                                }
                                help={
                                    errors.companyName && touched.companyName
                                        ? errors.companyName
                                        : null
                                }
                            >
                                <Input
                                    name="companyName"
                                    value={values.companyName}
                                    onChange={handleChange}
                                    data-testid="companyNameInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.companyCode", {ns: "common"})}
                                validateStatus={
                                    errors.companyCode && touched.companyCode ? "error" : "success"
                                }
                                help={
                                    errors.companyCode && touched.companyCode
                                        ? errors.companyCode
                                        : null
                                }
                            >
                                <Input
                                    disabled={Boolean(companyId) && company.companyCode}
                                    name="companyCode"
                                    type="number"
                                    value={values.companyCode}
                                    onChange={handleChange}
                                    data-testid="companyCodeInput"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.city", {ns: "common"})}
                                validateStatus={errors.city && touched.city ? "error" : "success"}
                                help={errors.city && touched.city ? errors.city : null}
                            >
                                <CityFilter
                                    showLabel={false}
                                    value={values.city}
                                    onChange={value => setFieldValue("city", value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.county", {ns: "common"})}
                                validateStatus={
                                    errors.county && touched.county ? "error" : "success"
                                }
                                help={errors.county && touched.county ? errors.county : null}
                            >
                                <Select
                                    name="county"
                                    value={values.county}
                                    disabled={!values.city}
                                    onChange={value => setFieldValue("county", value)}
                                    showSearch
                                    data-testid="countySelect"
                                >
                                    {values.city &&
                                        counties[values.city]?.length &&
                                        arrayAlphabeticalSort(counties[values.city]).map(county => (
                                            <Select.Option
                                                key={county.value}
                                                value={county.value}
                                                data-testid="countySelect_option"
                                            >
                                                {county.name}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.address", {ns: "common"})}
                        validateStatus={errors.address && touched.address ? "error" : "success"}
                        help={errors.address && touched.address ? errors.address : null}
                    >
                        <Input.TextArea
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            data-testid="addressInput"
                        />
                    </Form.Item>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.taxOffice", {ns: "common"})}
                                validateStatus={
                                    errors.registeredTaxOffice && touched.registeredTaxOffice
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.registeredTaxOffice && touched.registeredTaxOffice
                                        ? errors.registeredTaxOffice
                                        : null
                                }
                            >
                                <Input
                                    name="registeredTaxOffice"
                                    value={values.registeredTaxOffice}
                                    onChange={handleChange}
                                    data-testid="registeredTaxOfficeInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.taxNumber", {ns: "common"})}
                                validateStatus={
                                    errors.taxNumber && touched.taxNumber ? "error" : "success"
                                }
                                help={
                                    errors.taxNumber && touched.taxNumber ? errors.taxNumber : null
                                }
                            >
                                <Input
                                    name="taxNumber"
                                    value={values.taxNumber}
                                    onChange={handleChange}
                                    data-testid="taxNumberInput"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.bankName", {ns: "common"})}
                                validateStatus={
                                    errors.bankName && touched.bankName ? "error" : "success"
                                }
                                help={errors.bankName && touched.bankName ? errors.bankName : null}
                            >
                                <Input
                                    name="bankName"
                                    value={values.bankName}
                                    onChange={handleChange}
                                    data-testid="bankNameInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.iban", {ns: "common"})}
                                validateStatus={errors.iban && touched.iban ? "error" : "success"}
                                help={errors.iban && touched.iban ? errors.iban : null}
                            >
                                <Input
                                    name="iban"
                                    value={values.iban}
                                    data-testid="ibanInput"
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.billInformation", {ns: "common"})}
                        validateStatus={
                            errors.billInformation && touched.billInformation ? "error" : "success"
                        }
                        help={
                            errors.billInformation && touched.billInformation
                                ? errors.billInformation
                                : null
                        }
                    >
                        <Input.TextArea
                            name="billInformation"
                            value={values.billInformation}
                            onChange={handleChange}
                            data-testid="billInformationInput"
                        />
                    </Form.Item>
                    <Row justify="space-between">
                        <Col span="24">
                            <Form.Item
                                label={t("fields.courierTypes", {ns: "common"})}
                                validateStatus={
                                    errors.courierTypes && touched.courierTypes
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.courierTypes && touched.courierTypes
                                        ? errors.courierTypes
                                        : null
                                }
                            >
                                <Select
                                    name="courierTypes"
                                    mode="multiple"
                                    value={values.courierTypes}
                                    data-testid="courierTypesSelect"
                                    onChange={value => setFieldValue("courierTypes", value)}
                                >
                                    {courierTypes.map(type => (
                                        <Select.Option
                                            key={type.value}
                                            value={type.value}
                                            data-testid="courierTypesSelect_option"
                                        >
                                            {t(type.translation_key, {ns: "courierTypes"})}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="space-between">
                        <Col span="24">
                            <Form.Item
                                label={t("fields.companyWorkingTypes", {ns: "common"})}
                                validateStatus={
                                    errors.courierTypes && touched.courierTypes
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.courierTypes && touched.courierTypes
                                        ? errors.courierTypes
                                        : null
                                }
                            >
                                <Select
                                    name="workingTypes"
                                    mode="multiple"
                                    value={values.workingTypes}
                                    data-testid="companyWorkingType"
                                    onChange={value => setFieldValue("workingTypes", value)}
                                >
                                    {workingTypes.map(type => (
                                        <Select.Option
                                            key={type.value}
                                            value={type.value}
                                            data-testid="companyWorkingType_option"
                                        >
                                            {t(type.translation_key, {ns: "companyWorkingTypes"})}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="space-between">
                        <Col span="24">
                            <Form.Item
                                label={t("fields.smartLockerProvider", {ns: "common"})}
                                validateStatus={
                                    errors.smartLockerProvider && touched.smartLockerProvider
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.smartLockerProvider && touched.smartLockerProvider
                                        ? errors.smartLockerProvider
                                        : null
                                }
                            >
                                <Select
                                    name="smartLockerProvider"
                                    value={values.smartLockerProvider}
                                    data-testid="smartLockerProviderSelect"
                                    onChange={value => setFieldValue("smartLockerProvider", value)}
                                >
                                    {smartLockerProviders.map(type => (
                                        <Select.Option
                                            key={type.value}
                                            value={type.value}
                                            data-testid="smartLockerProviderSelect_option"
                                        >
                                            {type.name || type.value}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.targetDeliveryTime", {ns: "common"})}
                                validateStatus={
                                    errors.targetDeliveryTime && touched.targetDeliveryTime
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.targetDeliveryTime && touched.targetDeliveryTime
                                        ? errors.targetDeliveryTime
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name="targetDeliveryTime"
                                    value={values.targetDeliveryTime}
                                    onChange={handleChange}
                                    data-testid="targetDeliveryTimeInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("newCompany.fields.locationUpdateInterval", {
                                    ns: "newCompany"
                                })}
                                validateStatus={
                                    errors.locationUpdateInterval && touched.locationUpdateInterval
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.locationUpdateInterval && touched.locationUpdateInterval
                                        ? errors.locationUpdateInterval
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name="locationUpdateInterval"
                                    value={values.locationUpdateInterval}
                                    onChange={handleChange}
                                    data-testid="locationUpdateIntervalInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("newCompany.fields.heartbeatInterval", {ns: "newCompany"})}
                                validateStatus={
                                    errors.heartbeatInterval && touched.heartbeatInterval
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.heartbeatInterval && touched.heartbeatInterval
                                        ? errors.heartbeatInterval
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name="heartbeatInterval"
                                    value={values.heartbeatInterval}
                                    onChange={handleChange}
                                    data-testid="heartbeatIntervalInput"
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("newCompany.fields.courierSearchDistance", {
                                    ns: "newCompany"
                                })}
                                validateStatus={
                                    errors.courierSearchDistance && touched.courierSearchDistance
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.courierSearchDistance && touched.courierSearchDistance
                                        ? errors.courierSearchDistance
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name="courierSearchDistance"
                                    value={values.courierSearchDistance}
                                    onChange={handleChange}
                                    data-testid="courierSearchDistanceInput"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {stores.meStore.checkRole("newCompany") && (
                        <>
                            <Row justify="space-between">
                                <Col span="7">
                                    <Form.Item
                                        label={t("newCompany.fields.callbackUrl", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Input
                                            name="callback.url"
                                            value={values.callback.url}
                                            onChange={handleChange}
                                            data-testid="callbackUrlInput"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span="7">
                                    <Form.Item
                                        label={t("newCompany.fields.callbackTokenHeader", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Input
                                            name="callback.tokenHeader"
                                            value={values.callback.tokenHeader}
                                            onChange={handleChange}
                                            data-testid="callbackTokenHeaderInput"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span="7">
                                    <Form.Item
                                        label={t("newCompany.fields.callbackToken", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Input
                                            name="callback.token"
                                            value={values.callback.token}
                                            onChange={handleChange}
                                            data-testid="callbackTokenInput"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="space-between">
                                <Col span={11}>
                                    <Form.Item
                                        label={t("newCompany.fields.apiIntegration", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Switch
                                            checked={values.isApiIntegrationActive}
                                            checkedChildren={t(
                                                "newCompany.fields.yesValidateSubcompany",
                                                {ns: "newCompany"}
                                            )}
                                            unCheckedChildren={t(
                                                "newCompany.fields.noValidateSubcompany",
                                                {ns: "newCompany"}
                                            )}
                                            data-testid="isApiIntegrationActiveSwitch"
                                            onChange={checked =>
                                                setFieldValue("isApiIntegrationActive", checked)
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={11}>
                                    <Form.Item
                                        label={t("newCompany.fields.manuelDedication", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Switch
                                            checked={values.isManuelDedicationActive}
                                            checkedChildren={t(
                                                "newCompany.fields.yesValidateSubcompany",
                                                {ns: "newCompany"}
                                            )}
                                            unCheckedChildren={t(
                                                "newCompany.fields.noValidateSubcompany",
                                                {ns: "newCompany"}
                                            )}
                                            data-testid="isManuelDedicationActiveSwitch"
                                            onChange={checked =>
                                                setFieldValue("isManuelDedicationActive", checked)
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="space-between">
                                <Col span={11}>
                                    <Form.Item
                                        label={t("fields.testUser", {
                                            ns: "common"
                                        })}
                                    >
                                        <Switch
                                            checked={values.isTestUser}
                                            data-testid="isTestUserSwitch"
                                            onChange={checked =>
                                                setFieldValue("isTestUser", checked)
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={11}>
                                    <Form.Item
                                        label={t("newCompany.fields.preparedOrder", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <Switch
                                            checked={values.isOrderPreparedActive}
                                            data-testid="isOrderPreparedActiveSwitch"
                                            onChange={checked =>
                                                setFieldValue("isOrderPreparedActive", checked)
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Row justify="space-between">
                        {stores.meStore.checkRole("newCompany") && (
                            <Col span={11}>
                                <Form.Item
                                    label={t("newCompany.fields.validateSubcompanies", {
                                        ns: "newCompany"
                                    })}
                                >
                                    <Switch
                                        checked={values.validateSubCompanies}
                                        checkedChildren={t(
                                            "newCompany.fields.yesValidateSubcompany",
                                            {ns: "newCompany"}
                                        )}
                                        unCheckedChildren={t(
                                            "newCompany.fields.noValidateSubcompany",
                                            {ns: "newCompany"}
                                        )}
                                        data-testid="validateSubCompaniesSwitch"
                                        onChange={checked =>
                                            setFieldValue("validateSubCompanies", checked)
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={11}>
                            <Form.Item label={t("fields.companyMarkerImage", {ns: "common"})}>
                                <Row align="middle">
                                    <Upload
                                        customRequest={dummyRequest}
                                        listType="picture"
                                        multiple={false}
                                        maxCount={1}
                                        accept=".png,.jpg,.jpeg,.heic,.hevc,.tiff,.gif,.pdf"
                                        onRemove={() => {
                                            if (company?.data?.markerImage) {
                                                setCompanyMarkerImage(company?.data?.markerImage);
                                            } else {
                                                onRemoveMarkerImage();
                                            }
                                        }}
                                    >
                                        <Button
                                            data-testid="uploadButton"
                                            icon={
                                                companyMarkerImage && company?.data?.markerImage ? (
                                                    <MarkerImg
                                                        src={company?.data?.markerImageSrc}
                                                    />
                                                ) : (
                                                    <UploadOutlined />
                                                )
                                            }
                                        >
                                            {companyMarkerImage
                                                ? t("newCompany.actions.change", {ns: "newCompany"})
                                                : t("newCompany.actions.load", {ns: "newCompany"})}
                                        </Button>
                                    </Upload>
                                    {companyMarkerImage &&
                                    companyMarkerImage === company?.data?.markerImage ? (
                                        <DeleteIcon>
                                            <DeleteOutlined
                                                data-testid="deleteButton"
                                                onClick={() => onRemoveMarkerImage()}
                                            />
                                        </DeleteIcon>
                                    ) : null}
                                </Row>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("newCompany.fields.assignmentByDeliveryPoint", {
                                    ns: "newCompany"
                                })}
                            >
                                <Switch
                                    checked={values.isAssignmentByDeliveryPointActive}
                                    checkedChildren={t("newCompany.fields.yesValidateSubcompany", {
                                        ns: "newCompany"
                                    })}
                                    unCheckedChildren={t("newCompany.fields.noValidateSubcompany", {
                                        ns: "newCompany"
                                    })}
                                    data-testid="isAssignmentByDeliveryPointActiveSwitch"
                                    onChange={checked =>
                                        setFieldValue("isAssignmentByDeliveryPointActive", checked)
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={11}>
                            <Form.Item
                                label={t("newCompany.fields.sellerCoordinateRequirement", {
                                    ns: "newCompany"
                                })}
                            >
                                <Switch
                                    checked={values.isSellerCoordinateRequirementActive}
                                    checkedChildren={t("newCompany.fields.yesValidateSubcompany", {
                                        ns: "newCompany"
                                    })}
                                    unCheckedChildren={t("newCompany.fields.noValidateSubcompany", {
                                        ns: "newCompany"
                                    })}
                                    data-testid="isSellerCoordinateRequirementActiveSwitch"
                                    onChange={checked =>
                                        setFieldValue(
                                            "isSellerCoordinateRequirementActive",
                                            checked
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row type="flex" justify="end">
                        <CustomButton
                            type="primary"
                            htmlType="submit"
                            disabled={isSubmitting}
                            data-testid="submitButton"
                        >
                            {t("actions.save", {ns: "common"})}
                        </CustomButton>
                    </Row>
                </Form>
            </Col>
        </Row>
    ));
};
